<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <!-- Navigatiebalk -->
    <div class="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-flex-wrap">
      <!-- Breadcrumb drill-down -->
      <div class="tw-flex tw-items-center tw-gap-1 tw-text-sm">
        <button
          @click="setLevel('year')"
          class="tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded"
          :class="level === 'year' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-100 tw-text-blue-600 hover:tw-bg-gray-200'"
        >
          {{ viewYear }}
        </button>
        <template v-if="level !== 'year'">
          <span class="tw-text-gray-400">›</span>
          <button
            @click="setLevel('month')"
            class="tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded"
            :class="level === 'month' ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-100 tw-text-blue-600 hover:tw-bg-gray-200'"
          >
            {{ monthNames[viewMonth] }}
          </button>
        </template>
        <template v-if="level === 'day'">
          <span class="tw-text-gray-400">›</span>
          <button class="tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded tw-bg-blue-600 tw-text-white">
            {{ viewDay }}
          </button>
        </template>
      </div>

      <!-- Pijlen navigatie -->
      <button @click="navigatePrev" class="tw-px-2 tw-py-0.5 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded tw-text-gray-600">◄</button>
      <button @click="navigateNext" class="tw-px-2 tw-py-0.5 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded tw-text-gray-600">►</button>
      <button
        @click="goToToday"
        class="tw-px-3 tw-py-0.5 tw-bg-blue-500 hover:tw-bg-blue-600 tw-text-white tw-rounded tw-text-sm tw-font-medium"
      >
        VANDAAG
      </button>

      <!-- Neighbor toggle -->
      <button
        @click="neighborMeters.length > 0 ? (showNeighbors = !showNeighbors) : null"
        class="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors tw-flex tw-items-center tw-gap-1"
        :class="neighborMeters.length === 0
          ? 'tw-bg-gray-50 tw-text-gray-300 tw-cursor-default'
          : showNeighbors
            ? 'tw-bg-indigo-100 tw-text-indigo-700 hover:tw-bg-indigo-200'
            : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'"
        :title="neighborMeters.length === 0 ? 'Geen buren gevonden in dezelfde straat' : (showNeighbors ? 'Verberg buren' : 'Toon buren')"
      >
        <svg class="tw-w-3 tw-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        {{ neighborMatchType === 'postcode' ? 'Postcodegebied' : 'Buren' }} ({{ neighborMeters.length }})
      </button>

      <!-- Normalize toggle -->
      <button
        v-if="showNormalizeButton"
        @click="showNormalized = !showNormalized"
        class="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors"
        :class="showNormalized
          ? 'tw-bg-indigo-100 tw-text-indigo-700 hover:tw-bg-indigo-200'
          : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'"
        :title="showNormalized
          ? 'Genormaliseerd op Wh per kWp ge\u00EFnstalleerd vermogen. Hierdoor zijn installaties met verschillende aantallen panelen eerlijk vergelijkbaar.'
          : 'Normaliseer opbrengst naar Wh/kWp om installaties met verschillend vermogen te vergelijken'"
      >
        Wh/kWp
      </button>

      <!-- Active power toggle -->
      <button
        v-if="level === 'day'"
        @click="showActivePower = !showActivePower"
        class="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors tw-flex tw-items-center tw-gap-1"
        :class="showActivePower
          ? 'tw-bg-rose-100 tw-text-rose-700 hover:tw-bg-rose-200'
          : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'"
        :title="showActivePower ? 'Verberg actief vermogen' : 'Toon actief vermogen'"
      >
        <svg class="tw-w-3 tw-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
        Actief vermogen
      </button>

      <!-- Night toggle (day view) -->
      <button
        v-if="level === 'day'"
        @click="showFullDay = !showFullDay"
        class="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors tw-flex tw-items-center tw-gap-1"
        :class="showFullDay
          ? 'tw-bg-slate-200 tw-text-slate-700 hover:tw-bg-slate-300'
          : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'"
        :title="showFullDay
          ? 'Verberg de nacht: toon van een half uur voor zonsopkomst tot een half uur na zonsondergang'
          : 'Toon de hele dag, ook de nacht'"
      >
        <svg class="tw-w-3 tw-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
        Begin/einde
      </button>

      <!-- Previous period toggle -->
      <button
        @click="showPrevious = !showPrevious"
        :disabled="splitMeters.length > 0"
        class="tw-text-xs tw-px-2 tw-py-0.5 tw-rounded tw-transition-colors tw-flex tw-items-center tw-gap-1 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
        :class="showPrevious
          ? 'tw-bg-slate-200 tw-text-slate-700 hover:tw-bg-slate-300'
          : 'tw-bg-gray-100 tw-text-gray-500 hover:tw-bg-gray-200'"
        :title="splitMeters.length > 0
          ? 'Kan alleen bij de opgetelde productie, niet per meter'
          : showPrevious ? 'Verberg vergelijking' : `Vergelijk met ${previousLabel.toLowerCase()}`"
      >
        <svg class="tw-w-3 tw-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {{ previousLabel }}
      </button>

      <span class="tw-ml-auto tw-text-xs tw-text-gray-400">{{ levelLabel }}</span>
      <span
        v-if="isFetching || isFetchingPrevious || isFetchingSplit"
        class="tw-w-3.5 tw-h-3.5 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-flex-shrink-0"
      />
    </div>

    <!-- Chart -->
    <div class="tw-flex-1 tw-relative tw-min-h-0">
      <Bar v-if="chartData" :data="chartData" :options="chartOptions" />
    </div>

    <!-- Congestion legend (only in day view with intervals) -->
    <div v-if="hasCongestion" class="tw-mt-1 tw-flex tw-items-center tw-gap-3 tw-text-xs tw-text-gray-500 tw-flex-wrap">
      <span class="tw-flex tw-items-center tw-gap-1.5">
        <span class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-sm tw-border tw-border-amber-500" style="background-color: rgba(245, 158, 11, 0.22);" />
        Netcongestie (> 250 V)
      </span>
      <span v-if="hasCritical" class="tw-flex tw-items-center tw-gap-1.5">
        <span class="tw-inline-block tw-w-3 tw-h-3 tw-rounded-sm tw-border tw-border-red-500" style="background-color: rgba(239, 68, 68, 0.28);" />
        Kritiek (> 253 V)
      </span>
      <span class="tw-text-gray-400">
        — omvormer kan terugregelen door hoge netspanning
      </span>
    </div>

    <!-- Neighbor analysis (inside chart card) -->
    <div v-if="showNeighbors && activeNeighbors.length > 0" class="tw-mt-1">
      <div class="tw-text-xs tw-text-gray-400 tw-mb-1">
        <template v-if="neighborMatchType === 'postcode'">
          Vergelijking met installaties in hetzelfde postcodegebied met vergelijkbare oriëntatie en hellingshoek, genormaliseerd naar Wh/kWp.
        </template>
        <template v-else>
          Vergelijking met installaties in dezelfde straat. Gebruik de Wh/kWp knop om te normaliseren bij verschillend vermogen.
        </template>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useStorage } from '@vueuse/core'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Tooltip, Legend,
  BarElement, CategoryScale, LinearScale, BarController,
  LineElement, LineController, PointElement,
} from 'chart.js'
import { addDays, addMonths, addYears, format, min } from 'date-fns'
import fetchData from '@/util/fetch'
import { READINGS_API_BASE } from '@/components/Polling/api'
import { NEIGHBOR_COLORS } from './constants'
import { verticalLinesPlugin, congestionOverlayPlugin } from './chartPlugins'
import { useChartNavigation } from './composables/useChartNavigation'
import { useNeighborData } from './composables/useNeighborData'
import { useChartDatasets } from './composables/useChartDatasets'

ChartJS.register(
  CategoryScale, LinearScale,
  BarElement, BarController,
  LineElement, LineController, PointElement,
  Tooltip, Legend,
  verticalLinesPlugin,
  congestionOverlayPlugin
)

const props = defineProps({
  serialNumber: { type: String, required: true },
  // how much of the serial's address the main line covers: address (default, every meter there, a
  // CVZ's fake addresses too), installation or meter; neighbors always cover their own address
  scope: { type: String, default: 'address' },
  expectedProduction: { type: Number, default: null },
  numOfPanels: { type: Number, default: null },
  panelWp: { type: Number, default: null },
  neighborMeters: { type: Array, default: () => [] },
  neighborMatchType: { type: String, default: null },
  congestionIntervals: { type: Array, default: () => [] },
  // the address's meters separately instead of their sum: [{ serial, label }], empty for the sum
  splitMeters: { type: Array, default: () => [] },
})

const emit = defineEmits(['max-day-power', 'view-date-change'])

// ─── Navigation ──────────────────────────────────────────────────────────────
const {
  today, viewYear, viewMonth, viewDay, level,
  monthNames, levelLabel, dateRange,
  setLevel, navigatePrev, navigateNext, goToToday, navigateToDay,
} = useChartNavigation(emit)

// ─── Main data query ─────────────────────────────────────────────────────────
const queryKey = computed(() => [
  'meterGraph', props.serialNumber, props.scope,
  level.value, viewYear.value, viewMonth.value, viewDay.value,
])

// Readings of one period: quarter hours for a day, days for a month, months for a year
const fetchPeriod = async (periodLevel, { from, until }) => {
  if (periodLevel === 'day') {
    const urlParams = new URLSearchParams({
      date_from: from.toISOString(),
      date_until: until.toISOString(),
      scope: props.scope,
    })
    const result = await fetchData(
      `${READINGS_API_BASE}/meters/${props.serialNumber}/quarter-hour-deltas?${urlParams}`
    )
    return result?.status === 'OK' ? result.data : []
  }
  const urlParams = new URLSearchParams({
    serial_number: props.serialNumber,
    date_from: format(from, 'yyyy-MM-dd'),
    date_till: format(min([until, today]), 'yyyy-MM-dd'),
    scope: props.scope,
  })
  if (periodLevel === 'month') urlParams.append('limit', '31')
  const path = periodLevel === 'month' ? 'daily-aggregates' : 'month-aggregates'
  const result = await fetchData(`${READINGS_API_BASE}/${path}?${urlParams}`)
  return result?.status === 'OK' ? result.data : []
}

const { data: rawData, isFetching } = useQuery({
  queryKey,
  queryFn: () => fetchPeriod(level.value, dateRange.value),
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  enabled: computed(() => !!props.serialNumber),
})

// ─── Per meter: every meter of the address in one /production call ─────────
const SPLIT_RESOLUTION = { day: 'quarter', month: 'day', year: 'month' }

// serial -> rows in the shape of the main series ({ date, delta_power } by day, { date, production } otherwise)
const fetchSplit = async (periodLevel, { from, until }) => {
  // month and year: whole UTC days, as the daily and monthly aggregates count them. date_until is exclusive,
  // so up to and including today (the summed series asks date_till=today, inclusive)
  const dayStart = (d) => `${format(d, 'yyyy-MM-dd')}T00:00:00Z`
  const [dateFrom, dateUntil] = periodLevel === 'day'
    ? [from.toISOString(), until.toISOString()]
    : [dayStart(from), dayStart(min([until, addDays(today, 1)]))]
  if (dateFrom >= dateUntil) return {}
  const urlParams = new URLSearchParams({
    resolution: SPLIT_RESOLUTION[periodLevel],
    date_from: dateFrom,
    date_until: dateUntil,
  })
  props.splitMeters.forEach((m) => urlParams.append('series', `meter:${m.serial}`))
  const result = await fetchData(`${READINGS_API_BASE}/production?${urlParams}`)
  if (result?.status !== 'OK') return {}
  return Object.fromEntries(props.splitMeters.map((m, i) => [
    m.serial,
    (result.data[i]?.rows ?? []).map((r) => periodLevel === 'day'
      ? { date: r.period, delta_power: r.production }
      : { date: r.period, production: r.production }),
  ]))
}

const { data: splitDataMap, isFetching: isFetchingSplit } = useQuery({
  queryKey: computed(() => [
    'meterGraphSplit', props.splitMeters.map((m) => m.serial).join(','),
    level.value, viewYear.value, viewMonth.value, viewDay.value,
  ]),
  queryFn: () => fetchSplit(level.value, dateRange.value),
  refetchOnWindowFocus: false,
  keepPreviousData: true,
  enabled: computed(() => props.splitMeters.length > 0),
})
const splitMeters = computed(() => props.splitMeters)

// ─── Day view: the whole day or only daylight (plus half an hour either side) ──
const showFullDay = useStorage('polling-chart-show-full-day-v1', false)

// ─── Previous period (comparison) ────────────────────────────────────────────
const showPrevious = useStorage('polling-chart-show-previous-v1', false)
const previousLabel = computed(() =>
  level.value === 'year' ? 'Vorig jaar' : level.value === 'month' ? 'Vorige maand' : 'Vorige dag'
)
const previousRange = computed(() => {
  const { from } = dateRange.value
  const start = level.value === 'year' ? addYears(from, -1)
    : level.value === 'month' ? addMonths(from, -1)
    : addDays(from, -1)
  return { from: start, until: from }
})

const { data: previousRawData, isFetching: isFetchingPrevious } = useQuery({
  queryKey: computed(() => [
    'meterGraph', props.serialNumber, props.scope, level.value, 'previous', previousRange.value.from.toISOString(),
  ]),
  queryFn: () => fetchPeriod(level.value, previousRange.value),
  refetchOnWindowFocus: false,
  enabled: computed(() => !!props.serialNumber && showPrevious.value),
})

// ─── Neighbor data ───────────────────────────────────────────────────────────
const showActivePower = ref(false)
const {
  showNeighbors, showNormalized,
  mainTotalWp, activeNeighbors, neighborDataMap,
  neighborWpMap, neighborDetailMap,
  showNormalizeButton, normalizeValues,
} = useNeighborData(props, level, dateRange)

// ─── Chart datasets & options ────────────────────────────────────────────────
const { chartData, chartOptions } = useChartDatasets({
  props, level, viewYear, viewMonth, viewDay, rawData, monthNames, emit,
  showNeighbors, showActivePower, showNormalized,
  showPrevious, previousRawData, previousRange, showFullDay,
  activeNeighbors, neighborDataMap, neighborWpMap,
  mainTotalWp, normalizeValues,
  splitMeters, splitDataMap,
})

const hasCongestion = computed(() =>
  level.value === 'day' && (props.congestionIntervals?.length ?? 0) > 0
)
const hasCritical = computed(() =>
  hasCongestion.value && props.congestionIntervals.some(i => i.severity === 'critical')
)

defineExpose({
  showNeighbors,
  activeNeighbors,
  neighborDetailMap,
  neighborWpMap,
  NEIGHBOR_COLORS,
  navigateToDay,
})
</script>
